import styled from "styled-components";
import { colors } from "../../../utils/theme";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Button, Input } from "antd";
import { getPostHogClient } from "../../../helpers/posthogManager";
import { useLockScroll } from "../../hooks/useLockScroll";
import { useOverlay } from "../../../context/OverlayContext";
import { useProducts } from "../../hooks/firestore/useProducts";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import {
  filterHiddenProducts,
  getDiscountPrice,
  getEffectiveOrderUnit,
  searchProducts,
  sortProductsByPopular,
} from "../../../helpers/product";
import { Link, useNavigate } from "react-router-dom";
import urls from "../../../urls";
import { SearchOutlined } from "@ant-design/icons";
import { PRODUCT_PLACEHOLDER_SRC } from "../../../constants/ProductConstants";
import LazyImage from "../../image/LazyImage";
import { PRODUCT_DETAIL_SMALL_IMAGE } from "../../../constants/ImageConstants";
import { device } from "../../../utils/breakpoints";
import { CURRENCY } from "../../../constants/DeliveryConstants";
import { formatPrice } from "../../../helpers/formatters";
import useIsMobile from "../../hooks/useIsMobile";

export default function SiteWideSearch({ hasOffset = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { setShowOverlay } = useOverlay();
  const { data: products } = useProducts();
  const posthog = getPostHogClient();

  // State
  const [searchActive, setSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productsToShow, setProductsToShow] = useState([]);

  // Refs
  const searchRef = useRef(null);
  const debouncedTrackSearch = useRef(
    debounce((query) => {
      if (query && query.length >= 3 && posthog) {
        posthog.capture("product_search", {
          query: query.trim(),
          location: "desktop_header_search",
          method: "typing",
        });
      }
    }, 1000),
  ).current;

  useLockScroll(isMobile && searchActive);

  const handleSearch = (value) => {
    setSearchQuery(value);
    const filteredProducts = searchProducts(products, value);
    const filteredProductsByHidden = filterHiddenProducts(filteredProducts);
    const sortedProductsByPopular = sortProductsByPopular(
      filteredProductsByHidden,
    );
    setFilteredProducts(sortedProductsByPopular);
    if (sortedProductsByPopular.length > 0) {
      const firstResults = sortedProductsByPopular.slice(0, 12);
      setProductsToShow(firstResults);
    } else {
      setProductsToShow([]);
    }
    debouncedTrackSearch(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && searchQuery) {
      if (posthog && searchQuery.length >= 3) {
        posthog.capture("product_search", {
          query: searchQuery.trim(),
          location: "desktop_header_search",
          method: "submit",
        });
      }
      handleClickMoreResults();
    }
  };

  const handleClickMoreResults = () => {
    searchRef.current.blur();
    navigate(`${urls.shop}?search=${searchQuery}`);
  };

  useEffect(() => {
    setShowOverlay(searchActive);
    if (searchActive && searchRef.current) {
      searchRef.current.focus();

      const handleEscKey = (e) => {
        if (e.key === "Escape") {
          setSearchQuery(null);
          searchRef.current.blur();
        }
      };
      document.addEventListener("keydown", handleEscKey);

      return () => {
        document.removeEventListener("keydown", handleEscKey);
      };
    }
  }, [searchActive]);

  return (
    <>
      <IconButton onClick={() => setSearchActive(true)}>
        <SearchIcon />
      </IconButton>
      {searchActive && (
        <SearchBar>
          <div className={"container"}>
            <Input
              onChange={(e) => handleSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              ref={searchRef}
              value={searchQuery}
              type="search"
              allowClear
              onBlur={() => setSearchActive(false)}
              placeholder={t("search_placeholder")}
              prefix={<SearchOutlined />}
            />
            <IconButton onClick={() => setSearchActive(false)}>
              <CloseIcon />
            </IconButton>
          </div>
          {productsToShow.length > 0 && searchQuery && (
            <SearchResults onMouseDown={(e) => e.preventDefault()}>
              <ul>
                {productsToShow.map((product) => {
                  const discountedPrice = getDiscountPrice(
                    product.price,
                    product.discount,
                  );
                  const firstVariation = product.variations?.[0] || null;
                  const orderUnit = getEffectiveOrderUnit(product, firstVariation);

                  return (
                    <li key={product.id}>
                      <ProductLink
                        to={urls.product + product.url}
                        onClick={() => searchRef.current.blur()}
                      >
                        <Image
                          src={
                            !!product.image && !!product.image[0]
                              ? product.image[0]
                              : PRODUCT_PLACEHOLDER_SRC
                          }
                          alt={product.name}
                        />
                        <div>
                          <p className={"title"}>{product.name}</p>
                          <div className="product-price">
                            <p>
                              {CURRENCY +
                                formatPrice(discountedPrice || product.price)}
                              {orderUnit > 1 && (
                                <>
                                  {" "}
                                  <span className={"per_unit"}>
                                    {t("per_unit")}
                                  </span>
                                </>
                              )}
                              {discountedPrice !== null && (
                                <>
                                  {" "}
                                  <span className={"old_price"}>
                                    {CURRENCY + formatPrice(product.price)}
                                  </span>
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                      </ProductLink>
                    </li>
                  );
                })}
                {productsToShow.length < filteredProducts.length && (
                  <MoreResultsButton
                    type={isMobile ? "primary" : "text"}
                    block={isMobile}
                    size={isMobile ? "large" : "middle"}
                    onClick={handleClickMoreResults}
                  >
                    {t("view_all_results")}
                  </MoreResultsButton>
                )}
              </ul>
            </SearchResults>
          )}
        </SearchBar>
      )}
    </>
  );
}

const Icon = styled.div`
  background-color: #333;
  transition: background-color 0.2s ease;
`;

const SearchIcon = styled(Icon)`
  width: 32px;
  height: 32px;
  -webkit-mask: url("/assets/img/button-icons/search.svg") no-repeat center;
  mask: url("/assets/img/button-icons/search.svg") no-repeat center / contain;
`;

const CloseIcon = styled(Icon)`
  width: 18px;
  height: 18px;
  -webkit-mask: url("/assets/img/button-icons/close.svg") no-repeat center;
  mask: url("/assets/img/button-icons/close.svg") no-repeat center / contain;
`;

const IconButton = styled.button`
  border: none;
  background: none;
  min-width: 24px;
  min-height: 24px;

  &:hover {
    ${Icon} {
      background-color: ${colors.primary};
    }
  }
`;

const SearchBar = styled.div`
  position: fixed;
  display: flex;
  top: 0;
  left: 0;
  width: 100%;
  height: 92px;
  border-bottom: 1px solid #ccc;

  background: #fff;
  padding: 1rem;
  z-index: 1000;

  .container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .container span:has(input) {
    max-width: 500px;
  }

  @media ${device.lg} {
    position: absolute;
    height: 94px;
  }
`;

const SearchResults = styled.div`
  --top-offset: 92px;
  position: absolute;
  top: var(--top-offset);
  left: 0;
  right: 0;
  margin-inline: auto;
  width: min(600px, 100%);
  height: calc(100svh - var(--top-offset));
  background: #fff;
  z-index: 1000;
  padding: 6px;
  overflow-y: auto;

  ul {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }

  @media screen and ${device.lg} {
    --top-offset: 92px;
    position: absolute;
    margin-top: 1rem;
    border-radius: 12px;
    height: unset; {
    box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.16),
    0 3px 6px -4px rgba(0, 0, 0, 0.24),
    0 9px 28px 8px rgba(0, 0, 0, 0.1);

    ul {
      gap: 1rem;
    }
  }
`;

const ProductLink = styled(Link)`
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  padding: 4px;
  gap: 6px;
  border-radius: 6px;

  &:hover {
    border-color: ${colors.primary};
  }

  p {
    margin: 0;
  }

  p.title {
    font-weight: 600;
    hyphens: auto;
  }

  .per_unit {
    font-size: 12px;
  }

  .old_price {
    color: #8e8e8e;
    font-size: 12px;
    text-decoration: line-through;
  }
`;

const Image = styled(LazyImage)`
  border-radius: 5px;
  max-height: ${PRODUCT_DETAIL_SMALL_IMAGE.sm.height + "px"};
  max-width: 71px;

  @media ${device.lg} {
    max-height: ${PRODUCT_DETAIL_SMALL_IMAGE.xl.height + "px"};
    max-width: 93px;
  }
`;

const MoreResultsButton = styled(Button)`
  grid-column: 1 / -1;
  justify-self: center;
`;
